import { useCallback, useEffect, useState } from 'react';
import { getCourseById, getCourseChapters, getCourseReviews } from '../services/courses';
import {
  mapCourseChapters,
  mapCourseDetail,
  mapCourseReviews,
} from '../utils/courseMappers';

export function useCourseDetail(courseId) {
  const [course, setCourse] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [reviews, setReviews] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCourseDetail = useCallback(async () => {
    if (!courseId) {
      setCourse(null);
      setChapters([]);
      setReviews(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const [detailRes, chaptersRes, reviewsRes] = await Promise.all([
        getCourseById(courseId),
        getCourseChapters(courseId),
        getCourseReviews(courseId),
      ]);

      const rawCourse = detailRes?.data;
      const mappedCourse = mapCourseDetail(rawCourse);
      const mappedChapters = mapCourseChapters(
        chaptersRes?.data?.chapters || [],
        rawCourse || {}
      );
      const mappedReviews = mapCourseReviews(reviewsRes?.data);

      setCourse(mappedCourse);
      setChapters(mappedChapters);
      setReviews(mappedReviews);
    } catch (e) {
      setError(e?.message || 'Failed to load course details');
      setCourse(null);
      setChapters([]);
      setReviews(null);
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  const refetchReviews = useCallback(async () => {
    if (!courseId) return;

    try {
      const reviewsRes = await getCourseReviews(courseId);
      setReviews(mapCourseReviews(reviewsRes?.data));
    } catch {
      // Keep existing reviews if refresh fails
    }
  }, [courseId]);

  useEffect(() => {
    fetchCourseDetail();
  }, [fetchCourseDetail]);

  return {
    course,
    chapters,
    reviews,
    loading,
    error,
    refetch: fetchCourseDetail,
    refetchReviews,
  };
}
